import moment from 'moment'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'

import {
  CostCenterAssignment,
  FormattedAssignment,
} from 'modules/cost_centers/components/CostCenterAssigments/lib/types'

import {
  hasUnassignedCostCenter,
  hasZeroDedication,
  validDedication,
} from 'modules/cost_centers/components/CostCenterAssigments/lib/dedication'
import { canBeMarkedAsActive } from './validations/canBeMarkedAsActive'
import { DateRange, assignmentOverlaps } from './validations/overlaps'

interface Props {
  selectedDateRange: DateRange
  currentAssignments: FormattedAssignment[]
  displayedAssignments: FormattedAssignment[]
  allAssignments: CostCenterAssignment[]
  userInteracted: boolean
  hasNewStructureEnabled?: boolean
  isCreating?: boolean
  assignmentIdsBeingUpdated?: string[]
}

export const useAssignmentsValidator = ({
  selectedDateRange,
  displayedAssignments,
  currentAssignments,
  allAssignments,
  userInteracted,
  hasNewStructureEnabled = false,
  isCreating = false,
  assignmentIdsBeingUpdated = [],
}: Props) => {
  const defaultAssignmentsIds = currentAssignments.map((assignment) => assignment.id)
  const isCurrentActive =
    currentAssignments.length > 0 && currentAssignments.every((i) => i.endDate === undefined)

  const allAssignmentsExcludingCurrent = allAssignments.filter(
    (assignment) =>
      !defaultAssignmentsIds.includes(String(assignment.id)) &&
      !assignmentIdsBeingUpdated.includes(String(assignment.id))
  )

  const assignedDates = useUnsafeMemo(() => {
    return allAssignmentsExcludingCurrent.map((v) => ({
      startDate: v.startDate,
      endDate: v.endDate || undefined,
    }))
  }, [allAssignmentsExcludingCurrent])

  const overlappedAssignments = useUnsafeMemo(() => {
    return assignmentOverlaps({
      assignedDates,
      selectedDateRange,
    })
  }, [assignedDates, selectedDateRange])

  const assignmentCanBeMarkedAsActive = useUnsafeMemo(() => {
    return canBeMarkedAsActive({
      allAssignments,
      currentAssignments,
      isCurrentActive,
      startDate: selectedDateRange.startDate,
    })
  }, [allAssignments, currentAssignments, isCurrentActive, selectedDateRange])

  // Check if start date is in the past (only for structured cost center assignments in creation mode)
  const startDateInPast = useUnsafeMemo(() => {
    if (!hasNewStructureEnabled || !isCreating || !selectedDateRange.startDate) {
      return false
    }
    const today = moment().startOf('day')
    const startDate = moment(selectedDateRange.startDate).startOf('day')
    return startDate.isBefore(today)
  }, [hasNewStructureEnabled, isCreating, selectedDateRange.startDate])

  const validations = useUnsafeMemo(() => {
    return {
      overlappedAssignments: overlappedAssignments,
      validDedication: validDedication(displayedAssignments),
      hasZeroDedication: hasZeroDedication(displayedAssignments),
      hasUnassignedCostCenter: hasUnassignedCostCenter(
        displayedAssignments,
        hasNewStructureEnabled
      ),
      canBeMarkedAsActive: assignmentCanBeMarkedAsActive,
      startDateInPast: startDateInPast,
    }
  }, [
    displayedAssignments,
    overlappedAssignments,
    assignmentCanBeMarkedAsActive,
    hasNewStructureEnabled,
    startDateInPast,
  ])

  const validForSave = useUnsafeMemo(() => {
    return (
      userInteracted &&
      validations.validDedication &&
      !validations.hasZeroDedication &&
      !validations.hasUnassignedCostCenter &&
      !validations.overlappedAssignments &&
      !validations.startDateInPast &&
      selectedDateRange.startDate != ''
    )
  }, [selectedDateRange, validations, userInteracted])

  return {
    validForSave,
    validations,
  }
}
